import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Export } from "@phosphor-icons/react";
import { api, apiError, fmtMoney } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DateField } from "@/components/DateField";

const CATEGORIES = ["labor", "equipment", "materials", "explosives", "services", "other"];
const emptyForm = { category: "labor", description: "", amount: 0, date: "", type: "actual" };

export default function ProjectCostsTab({ projectId, canManage, doExport }) {
  const { t } = useLang();
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/costs`).then((r) => setCosts(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/costs/summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount) };
      let res;
      if (editingId) res = await api.put(`/costs/${editingId}`, payload);
      else res = await api.post(`/projects/${projectId}/costs`, payload);
      const offline = res?.data?._offline;
      toast.success(offline ? t("savedOffline") : "✓");
      setOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      if (offline) {
        setCosts((prev) => editingId
          ? prev.map((x) => (x.id === editingId ? { ...x, ...res.data, id: editingId } : x))
          : [res.data, ...prev]);
      } else {
        load();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (costId) => {
    if (!window.confirm(t("confirmDelete"))) return;
    const res = await api.delete(`/costs/${costId}`);
    if (res?.data?._offline) {
      setCosts((p) => p.filter((x) => x.id !== costId));
      toast.success(t("savedOffline"));
    } else {
      load();
    }
  };

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="kpi-card bg-white border border-slate-200 rounded-lg p-6">
            <p className="overline-label">{t("budget")}</p>
            <p className="font-heading font-black text-2xl mt-2">{fmtMoney(summary.budget)}</p>
          </div>
          <div className="kpi-card bg-white border border-slate-200 rounded-lg p-6">
            <p className="overline-label">{t("planned")}</p>
            <p className="font-heading font-black text-2xl mt-2">{fmtMoney(summary.total_planned)}</p>
          </div>
          <div className="kpi-card bg-white border border-slate-200 rounded-lg p-6">
            <p className="overline-label">{t("actual")}</p>
            <p className="font-heading font-black text-2xl mt-2 text-amber-600">{fmtMoney(summary.total_actual)}</p>
          </div>
          <div className="kpi-card bg-white border border-slate-200 rounded-lg p-6">
            <p className="overline-label">{t("remaining")}</p>
            <p className={`font-heading font-black text-2xl mt-2 ${summary.budget - summary.total_actual < 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtMoney(summary.budget - summary.total_actual)}</p>
          </div>
        </div>
      )}

      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-cost-button" onClick={() => { setEditingId(null); setForm(emptyForm); }} className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={16} weight="bold" /> {t("newCost")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">{editingId ? t("edit") : t("newCost")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="overline-label">{t("category")}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="cost-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(c)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="cost-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="actual">{t("actual")}</SelectItem>
                      <SelectItem value="planned">{t("planned")}</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("amount")} (USD)</Label>
                  <Input data-testid="cost-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("date")}</Label>
                  <DateField testid="cost-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
                <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("description")}</Label>
                  <Input data-testid="cost-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <Button data-testid="cost-save-button" onClick={save} disabled={!form.amount || !form.date} className="bg-blue-900 hover:bg-blue-800 w-full">{editingId ? t("update") : t("create")}</Button>
            </DialogContent>
          </Dialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-costs-button" className="gap-2"><Export size={16} weight="bold" /> {t("export")}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white" align="start">
              <DropdownMenuItem data-testid="export-costs-pdf-portrait" onClick={() => doExport("costs", "pdf", "portrait")}>{t("costs")} — PDF ({t("orientPortrait")})</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-costs-pdf-landscape" onClick={() => doExport("costs", "pdf", "landscape")}>{t("costs")} — PDF ({t("orientLandscape")})</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-costs-xlsx" onClick={() => doExport("costs", "xlsx")}>{t("costs")} — Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg">
        <table className="w-full data-table">
          <thead><tr><th>{t("date")}</th><th>{t("category")}</th><th>{t("description")}</th><th>{t("type")}</th><th className="text-right">{t("amount")}</th>{canManage && <th></th>}</tr></thead>
          <tbody data-testid="costs-table-body">
            {costs.map((c) => (
              <tr key={c.id}>
                <td className="font-mono text-xs">{c.date}</td>
                <td className="text-xs uppercase tracking-wider font-semibold text-slate-600">{t(c.category)}</td>
                <td>{c.description}</td>
                <td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${c.type === "actual" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{t(c.type)}</span></td>
                <td className="text-right font-medium">{fmtMoney(c.amount)}</td>
                {canManage && (
                  <td className="whitespace-nowrap">
                    <button data-testid={`cost-edit-${c.id}`} className="text-slate-400 hover:text-blue-900 transition-colors mr-2"
                      onClick={() => {
                        setEditingId(c.id);
                        setForm({ category: c.category, description: c.description, amount: c.amount, date: c.date, type: c.type });
                        setOpen(true);
                      }}><PencilSimple size={16} /></button>
                    <button data-testid={`cost-delete-${c.id}`} onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash size={16} /></button>
                  </td>
                )}
              </tr>
            ))}
            {!costs.length && <tr><td colSpan={6} className="text-center text-slate-400 py-10">{t("noData")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
